import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
    RadarChart,
    Radar,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import { Home, ArrowLeft } from "lucide-react";

interface AreaScore {
    area: string;
    userScore: number;
    collegeScore?: number;
    globalScore?: number;
    count: number;
}

const UserAnalytics = () => {
    const { user, profile, isLoading } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [areaScores, setAreaScores] = useState<AreaScore[]>([]);
    const [stats, setStats] = useState({
        totalAttempts: 0,
        completedAttempts: 0,
        avgScore: 0,
        bestArea: "",
        weakestArea: "",
    });
    const [collegeName, setCollegeName] = useState<string>("");

    useEffect(() => {
        if (!isLoading && !user) {
            navigate("/login");
        }
    }, [user, isLoading, navigate]);

    useEffect(() => {
        if (user) {
            fetchUserAnalytics();
        }
    }, [user]);

    const fetchUserAnalytics = async () => {
        setLoading(true);

        // 1. Fetch User Attempts
        const { data: attempts } = await supabase
            .from("attempts")
            .select("*")
            .eq("user_id", user?.id)
            .order("started_at", { ascending: false });

        if (attempts && attempts.length > 0) {
            const completed = attempts.filter(a => a.completed);
            const avgScore = completed.length > 0
                ? completed.reduce((sum, a) => sum + (a.overall_score || 0), 0) / completed.length
                : 0;

            // Use the latest completed attempt for area scores (same as dashboard)
            const latestAttempt = attempts.find(a => a.completed) || attempts[0];
            let userAreaMap = new Map<string, { score: number; count: number }>();

            if (latestAttempt) {
                // Fetch responses for the latest attempt
                const { data: responses } = await supabase
                    .from("responses")
                    .select("competence_area, rating")
                    .eq("attempt_id", latestAttempt.id);

                if (responses) {
                    // Define the 5 competence areas
                    const areas = [
                        "Information & Data Literacy",
                        "Communication & Collaboration",
                        "Digital Content Creation",
                        "Safety",
                        "Problem Solving"
                    ];

                    // Calculate score for each area using the same logic as dashboard
                    areas.forEach(area => {
                        const areaResponses = responses.filter(r => r.competence_area === area);
                        const numQuestions = areaResponses.length;

                        if (numQuestions > 0) {
                            const totalRating = areaResponses.reduce((sum, r) => sum + r.rating, 0);
                            const maxScore = numQuestions * 8;
                            const minScore = numQuestions * 1;
                            let score = 0;
                            if (maxScore - minScore !== 0) {
                                score = ((totalRating - minScore) / (maxScore - minScore)) * 100;
                            }

                            userAreaMap.set(area, {
                                score: isNaN(score) ? 0 : Math.max(0, Math.min(100, score)),
                                count: numQuestions
                            });
                        }
                    });
                }
            }

            // 3. Fetch Comparative Data (Global & College)
            // Fetch all completed attempts
            const { data: allAttempts } = await supabase
                .from("attempts")
                .select("id, completed")
                .eq("completed", true)
                .limit(500);

            let collegeScoresMap = new Map<string, { total: number; count: number }>();
            let globalScoresMap = new Map<string, { total: number; count: number }>();

            // Get user's college ID
            const userCollegeId = profile?.college_id;
            if (userCollegeId) {
                const { data: college } = await supabase
                    .from("colleges")
                    .select("name")
                    .eq("id", userCollegeId)
                    .single();
                if (college) setCollegeName(college.name);
            }

            if (allAttempts && allAttempts.length > 0) {
                // Fetch responses for all attempts
                const sampleAttemptIds = allAttempts.map(a => a.id);

                const { data: allResponses } = await supabase
                    .from("responses")
                    .select("competence_area, rating, attempt_id");

                // Fetch user profiles for all attempts to get college IDs
                const { data: allUsers } = await supabase
                    .from("profiles")
                    .select("id, college_id");

                if (allResponses && allUsers) {
                    // Build map of user_id -> college_id from profiles
                    const userCollegeMap = new Map<string, string>();
                    allUsers.forEach((p: any) => {
                        if (p.college_id) {
                            userCollegeMap.set(p.id, p.college_id);
                        }
                    });

                    // Build map of attempt_id -> user_id
                    const { data: attemptUsers } = await supabase
                        .from("attempts")
                        .select("id, user_id");

                    const attemptCollegeMap = new Map<string, string>();
                    if (attemptUsers) {
                        attemptUsers.forEach((a: any) => {
                            const colId = userCollegeMap.get(a.user_id);
                            if (colId) {
                                attemptCollegeMap.set(a.id, colId);
                            }
                        });
                    }

                    // Group responses by area for global and college averages
                    const areas = [
                        "Information & Data Literacy",
                        "Communication & Collaboration",
                        "Digital Content Creation",
                        "Safety",
                        "Problem Solving"
                    ];

                    areas.forEach(area => {
                        const areaResponses = allResponses.filter(r => r.competence_area === area);

                        if (areaResponses.length > 0) {
                            const totalRating = areaResponses.reduce((sum, r) => sum + r.rating, 0);
                            const numQuestions = areaResponses.length;
                            const maxScore = numQuestions * 8;
                            const minScore = numQuestions * 1;
                            let score = 0;
                            if (maxScore - minScore !== 0) {
                                score = ((totalRating - minScore) / (maxScore - minScore)) * 100;
                            }

                            globalScoresMap.set(area, {
                                total: isNaN(score) ? 0 : Math.max(0, Math.min(100, score)),
                                count: numQuestions
                            });
                        }

                        // College scores
                        const collegeResponses = areaResponses.filter(r => {
                            const colId = attemptCollegeMap.get(r.attempt_id);
                            return userCollegeId && colId === userCollegeId;
                        });

                        if (collegeResponses.length > 0) {
                            const totalRating = collegeResponses.reduce((sum, r) => sum + r.rating, 0);
                            const numQuestions = collegeResponses.length;
                            const maxScore = numQuestions * 8;
                            const minScore = numQuestions * 1;
                            let score = 0;
                            if (maxScore - minScore !== 0) {
                                score = ((totalRating - minScore) / (maxScore - minScore)) * 100;
                            }

                            collegeScoresMap.set(area, {
                                total: isNaN(score) ? 0 : Math.max(0, Math.min(100, score)),
                                count: numQuestions
                            });
                        }
                    });
                }
            }

            // 4. Merge Data
            const areas = [
                "Information & Data Literacy",
                "Communication & Collaboration",
                "Digital Content Creation",
                "Safety",
                "Problem Solving"
            ];

            const finalScores: AreaScore[] = areas.map(area => {
                const userData = userAreaMap.get(area);
                const userScore = userData?.score || 0;
                const count = userData?.count || 0;

                const gData = globalScoresMap.get(area);
                const globalScore = gData ? gData.total : 0;

                const cData = collegeScoresMap.get(area);
                const collegeScore = cData ? cData.total : 0;

                return {
                    area,
                    userScore,
                    globalScore,
                    collegeScore,
                    count
                };
            });

            setAreaScores(finalScores);
            const sortedScores = [...finalScores].filter(s => s.count > 0).sort((a, b) => b.userScore - a.userScore);

            setStats({
                totalAttempts: attempts.length,
                completedAttempts: completed.length,
                avgScore,
                bestArea: sortedScores[0]?.area || "N/A",
                weakestArea: sortedScores[sortedScores.length - 1]?.area || "N/A",
            });
        }

        setLoading(false);
    };

    if (isLoading || loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    const radarData = areaScores.map(area => ({
        area: area.area,
        userScore: area.userScore,
        collegeScore: area.collegeScore,
        globalScore: area.globalScore,
        fullMark: 100,
    }));

    return (
        <div className="min-h-screen bg-gradient-to-br from-background to-secondary py-8 px-4">
            <div className="max-w-7xl mx-auto">
                {/* Bilingual Header */}
                <Card className="p-6 mb-6 bg-primary/5 border-primary/20">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-primary mb-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                                My Analytics - تحليلاتي
                            </h1>
                            <p className="text-lg text-foreground">
                                {profile ? `${profile.first_name} ${profile.last_name}` : user?.email}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <Button
                                onClick={() => navigate("/dashboard")}
                                variant="outline"
                                className="gap-2"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Dashboard - لوحة التحكم
                            </Button>
                            <Button
                                onClick={() => navigate("/")}
                                variant="default"
                                className="gap-2"
                            >
                                <Home className="h-4 w-4" />
                                Home - الرئيسية
                            </Button>
                        </div>
                    </div>
                </Card>

                {/* Statistics Cards */}
                <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                    <Card className="p-6">
                        <h3 className="text-sm font-medium text-muted-foreground mb-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                            Total Attempts - المحاولات
                        </h3>
                        <p className="text-2xl font-bold text-primary">{stats.totalAttempts}</p>
                    </Card>

                    <Card className="p-6">
                        <h3 className="text-sm font-medium text-muted-foreground mb-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                            Completed - المكتمل
                        </h3>
                        <p className="text-2xl font-bold text-success">{stats.completedAttempts}</p>
                    </Card>

                    <Card className="p-6">
                        <h3 className="text-sm font-medium text-muted-foreground mb-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                            Average Score - المعدل
                        </h3>
                        <p className="text-2xl font-bold text-accent">{stats.avgScore.toFixed(1)}%</p>
                    </Card>

                    <Card className="p-6">
                        <h3 className="text-sm font-medium text-muted-foreground mb-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                            Best Area - الأفضل
                        </h3>
                        <p className="text-sm font-bold text-success">{stats.bestArea}</p>
                    </Card>

                    <Card className="p-6">
                        <h3 className="text-sm font-medium text-muted-foreground mb-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                            Weakest Area - الأضعف
                        </h3>
                        <p className="text-sm font-bold text-destructive">{stats.weakestArea}</p>
                    </Card>
                </div>

                {/* Charts */}
                <div className="grid lg:grid-cols-2 gap-6 mb-8">
                    <Card className="p-6">
                        <h3 className="text-xl font-bold mb-4 text-primary" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                            Competence Radar - رادار الكفاءة
                        </h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <RadarChart data={radarData}>
                                <PolarGrid stroke="hsl(var(--border))" />
                                <PolarAngleAxis dataKey="area" tick={{ fill: "hsl(var(--foreground))", fontSize: 12 }} />
                                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "hsl(var(--muted-foreground))" }} />
                                <Radar
                                    name="My Score"
                                    dataKey="userScore"
                                    stroke="hsl(var(--primary))"
                                    fill="hsl(var(--primary))"
                                    fillOpacity={0.5}
                                />
                                <Radar
                                    name="Global Avg"
                                    dataKey="globalScore"
                                    stroke="hsl(var(--muted-foreground))"
                                    fill="hsl(var(--muted-foreground))"
                                    fillOpacity={0.1}
                                />
                                {collegeName && (
                                    <Radar
                                        name="College Avg"
                                        dataKey="collegeScore"
                                        stroke="hsl(var(--accent))"
                                        fill="hsl(var(--accent))"
                                        fillOpacity={0.3}
                                    />
                                )}
                                <Legend />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: "hsl(var(--card))",
                                        border: "1px solid hsl(var(--border))",
                                        borderRadius: "var(--radius)",
                                    }}
                                />
                            </RadarChart>
                        </ResponsiveContainer>
                    </Card>

                    <Card className="p-6">
                        <h3 className="text-xl font-bold mb-4 text-primary" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                            Scores by Area - النتائج حسب المجال
                        </h3>
                        <ResponsiveContainer width="100%" height={350}>
                            <BarChart
                                data={areaScores}
                                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                <XAxis
                                    dataKey="area"
                                    tick={{ fill: "hsl(var(--foreground))", fontSize: 10 }}
                                    angle={-45}
                                    textAnchor="end"
                                    height={80}
                                />
                                <YAxis domain={[0, 100]} tick={{ fill: "hsl(var(--muted-foreground))" }} />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: "hsl(var(--card))",
                                        border: "1px solid hsl(var(--border))",
                                        borderRadius: "var(--radius)",
                                    }}
                                    formatter={(value: any) => typeof value === 'number' ? `${value.toFixed(1)}%` : 'N/A'}
                                />
                                <Legend />
                                <Bar dataKey="userScore" fill="hsl(var(--primary))" name="My Score" />
                                <Bar dataKey="globalScore" fill="hsl(var(--muted-foreground))" name="Global Avg" />
                                {collegeName && (
                                    <Bar dataKey="collegeScore" fill="hsl(var(--accent))" name="College Avg" />
                                )}
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                </div>

                {/* Detailed Scores Table - matching Dashboard format */}
                <Card className="p-6">
                    <h2 className="text-2xl font-semibold text-foreground mb-4">Detailed Scores - النتائج التفصيلية</h2>
                    {areaScores.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-border">
                                        <th className="text-left py-3 px-4 font-semibold">Area - المجال</th>
                                        <th className="text-left py-3 px-4 font-semibold">Score - النتيجة</th>
                                        <th className="text-left py-3 px-4 font-semibold">Questions - الأسئلة</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {areaScores.map((area, index) => (
                                        <tr key={index} className="border-b border-border/50">
                                            <td className="py-3 px-4">{area.area}</td>
                                            <td className="py-3 px-4">{area.userScore ? area.userScore.toFixed(1) + "%" : "-"}</td>
                                            <td className="py-3 px-4">{area.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default UserAnalytics;
